import { Component, type ErrorInfo, type ReactNode } from "react";
import { Box, Button, Paper, Typography } from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

interface ErrorBoundaryProps {
  children: ReactNode;
  /**
   * "page": themed fallback for use inside ThemeProvider (route content).
   * "bare": dependency-free fallback for the top-level boundary, which may
   * catch theme/provider construction crashes.
   */
  variant?: "page" | "bare";
  /** When this value changes (e.g. the pathname), the boundary resets. */
  resetKey?: unknown;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * Render-error guard. Before this existed, any uncaught render error
 * white-screened the whole app.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[ErrorBoundary] render error:", error, info.componentStack);
  }

  override componentDidUpdate(prevProps: ErrorBoundaryProps) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  override render() {
    if (!this.state.error) return this.props.children;

    if (this.props.variant === "bare") {
      return (
        <div style={{ padding: 32, fontFamily: "system-ui, sans-serif", textAlign: "center" }}>
          <h2>Something went wrong</h2>
          <p>An unexpected error occurred. Reloading usually fixes it.</p>
          <button onClick={() => window.location.reload()}>Reload</button>
        </div>
      );
    }

    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
          p: 3,
        }}
      >
        <Paper
          variant="outlined"
          sx={{
            p: 4,
            maxWidth: 480,
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
          }}
        >
          <ErrorOutlineIcon color="error" sx={{ fontSize: 48 }} />
          <Typography variant="h5">Something went wrong</Typography>
          <Typography variant="body2" color="text.secondary">
            An unexpected error occurred on this page. Your work on other
            pages is unaffected.
          </Typography>
          <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
            <Button variant="contained" onClick={() => window.location.reload()}>
              Reload
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                window.location.href = "/";
              }}
            >
              Go Home
            </Button>
          </Box>
        </Paper>
      </Box>
    );
  }
}
